import { Col, Row, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import { ModuleType } from '../../../api/types';
import { useCompany } from '../../../components/contexts/CompanyContextManager';
import ModuleSelectField from '../../../components/fields/ModuleSelectField';
import SingleUploadField from '../../../components/fields/SingleUploadField';
import CollapsibleWrapper, { Panel } from '../../../components/wrappers/CollapsibleWrapper';
import FormFields from '../../../themes/admin/Fields/FormFields';
import { allowedExtensions } from '../../../utilities/extensions';
import useSystemSwitchData from '../../../utilities/useSystemSwitchData';

const colSpan = { lg: 8, md: 12, xs: 24 };

type WebpageDetailsFormProps = {
    updated?: string;
    moduleDisplayName?: string;
    hasLiveChat?: boolean;
    offset?: string;
    disabled?: boolean;
};

const WebpageMainDetailsForm = ({
    updated,
    moduleDisplayName,
    hasLiveChat,
    offset,
    disabled = false,
}: WebpageDetailsFormProps) => {
    const company = useCompany(true);
    const { yesNoSwitch } = useSystemSwitchData();
    const { t } = useTranslation(['webpageDetails']);

    return (
        <Space direction="vertical" style={{ width: '100%' }}>
            <CollapsibleWrapper defaultActiveKey={t('webpageDetails:sectionTitle')}>
                <Panel key={t('webpageDetails:sectionTitle')} header={t('webpageDetails:sectionTitle')}>
                    <Row gutter={14}>
                        {!updated ? (
                            <Col {...colSpan}>
                                <ModuleSelectField
                                    {...t('webpageDetails:fields.module', { returnObjects: true })}
                                    companyId={company?.id}
                                    disabled={disabled}
                                    moduleTypes={[ModuleType.WebsiteModule]}
                                    name="moduleId"
                                    required
                                />
                            </Col>
                        ) : (
                            <Col {...colSpan}>
                                <FormFields.DisplayField
                                    {...t('webpageDetails:fields.module', { returnObjects: true })}
                                    value={moduleDisplayName}
                                />
                            </Col>
                        )}

                        <Col {...colSpan}>
                            <SingleUploadField
                                {...t('webpageDetails:fields.bannerImage', { returnObjects: true })}
                                disabled={disabled}
                                extensions={[...allowedExtensions.image, ...allowedExtensions.video]}
                                name="bannerImage"
                                sizeLimitInMiB={20}
                            />
                        </Col>

                        <Col {...colSpan}>
                            <SingleUploadField
                                {...t('webpageDetails:fields.mobileBannerImage', { returnObjects: true })}
                                disabled={disabled}
                                extensions={[...allowedExtensions.image, ...allowedExtensions.video]}
                                name="mobileBannerImage"
                                sizeLimitInMiB={20}
                            />
                        </Col>

                        <Col {...colSpan}>
                            <FormFields.TranslatedInputField
                                {...t('webpageDetails:fields.bannerTitle', { returnObjects: true })}
                                disabled={disabled}
                                name="bannerTitle"
                            />
                        </Col>
                        <Col {...colSpan}>
                            <FormFields.TranslatedInputField
                                {...t('webpageDetails:fields.bannerSubTitle', { returnObjects: true })}
                                disabled={disabled}
                                name="bannerSubTitle"
                            />
                        </Col>
                        <Col {...colSpan}>
                            <FormFields.TranslatedInputField
                                {...t('webpageDetails:fields.pageTitle', { returnObjects: true })}
                                disabled={disabled}
                                name="pageTitle"
                                required
                            />
                        </Col>
                        <Col {...colSpan}>
                            <FormFields.InputField
                                {...t('webpageDetails:fields.metaTitle', { returnObjects: true })}
                                disabled={disabled}
                                name="metaTitle"
                            />
                        </Col>
                        <Col {...colSpan}>
                            <FormFields.InputField
                                {...t('webpageDetails:fields.metaDescription', { returnObjects: true })}
                                disabled={disabled}
                                name="metaDescription"
                            />
                        </Col>
                        <Col {...colSpan}>
                            <FormFields.InputField
                                {...t('webpageDetails:fields.urlSlug', { returnObjects: true })}
                                disabled={disabled}
                                name="urlSlug"
                                required
                            />
                        </Col>
                        <Col {...colSpan}>
                            <FormFields.SwitchField
                                {...t('webpageDetails:fields.isActive', { returnObjects: true })}
                                disabled={disabled}
                                name="isActive"
                                {...yesNoSwitch}
                                required
                            />
                        </Col>

                        {hasLiveChat && (
                            <Col {...colSpan}>
                                <FormFields.SwitchField
                                    {...t('webpageDetails:fields.liveChatDisplay', { returnObjects: true })}
                                    disabled={disabled}
                                    name="liveChatDisplay"
                                    {...yesNoSwitch}
                                    required
                                />
                            </Col>
                        )}
                        {updated && (
                            <Col {...colSpan}>
                                <FormFields.DisplayFieldWithUTCOffset
                                    {...t('common:fields.updated', { returnObjects: true, offset })}
                                    value={updated}
                                />
                            </Col>
                        )}
                    </Row>
                </Panel>
            </CollapsibleWrapper>
        </Space>
    );
};

export default WebpageMainDetailsForm;
